/**
 * MobileShiftFormModal
 *
 * Mobile full-screen dialog replacement for ShiftFormModal.
 * Replicates ALL business logic from the desktop version but uses
 * MUI components with mobile-first UX patterns.
 *
 * Supported forms: SHIFT, LEAVE, SAMPLE_SHIFT, ADD_SAMPLE_SHIFT,
 *                  ADD_SAMPLE_LEAVE, ADD_USER
 */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Chip,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Langs from "../../../app/lang/langs";
import {
  isAdmin,
  getMinutesFromDate,
  getTagClass,
  isWorkingShift,
  isOwner,
  checkPermission,
} from "../../../app/utils/helpers";
import { config } from "../../../app/utils/config";
import {
  deleteItemFromPlanningList,
  hideFormPopup,
  setSelectedFormData,
  updateShiftProperties,
} from "../planningSlice";
import {
  showPopup,
  onPopupHandle,
} from "../../common/commonSlice";
import {
  deleteShiftLocally,
  updateShiftLocally,
  updateShifts,
  deleteShifts,
} from "../../../components/scheduler/shifts/shiftsSlice";
import {
  addShiftSample,
  deleteShiftSamples,
  getShiftSamples,
  updateShiftSample,
} from "../../../components/scheduler/shifts/sampleShiftsSlice";
import {
  addLeaveSample,
  deleteLeaveSamples,
  updateLeaveSample,
} from "../../../components/scheduler/leave/leaveSampleSlice";
import {
  addCollabToGroupe,
  addGroupeMembers,
  getMembers,
} from "../../../components/collabs/collabsSlice";
import moment from "moment";
import { ORANGE, severityToColor, SlideUp } from "./MobileShiftFormModal/constants";
import {
  CollabShiftView,
  ShiftBody,
  SampleShiftBody,
  SampleLeaveBody,
  AddUserBody,
  BottomBar,
} from "./MobileShiftFormModal/components";
import { timeStrToMinutes } from "./MobileShiftFormModal/utils/formHelpers";

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export default function MobileShiftFormModal(props) {
  const dispatch = useDispatch();

  /* ---- Redux selectors ---- */
  const settings = useSelector((state) => state.settings);
  const teams = useSelector((state) => state.teams.allTeams);
  const myTeam = useSelector((state) => state.teams.myTeam);
  const resources = useSelector((state) => state.collabs.collaboratorsList);
  const selectedForm = useSelector((state) => state.planning.selectedForm);
  const selectedGroupe = useSelector((state) => state.planning.selectedGroupe);
  const selectedDate = useSelector((state) => state.planning.selectedDate);
  const collabs = useSelector((state) => state.collabs);
  const currentGroupeId = useSelector((state) => state.collabs.id_groupe);
  const groupesList = useSelector((state) => state.groupes.list);
  const selectedTeamsIds = useSelector((state) => state.teams.selectedTeamsIds);
  const collabsList = useSelector((state) => state.collabs.list);
  const currentCollabsList = useSelector((state) => state.collabs.currentList);
  const sampleLeaves = useSelector((state) => state.sampleLeaves.list);
  const selectedFormData = useSelector(
    (state) => state.planning.selectedFormData
  );
  const confirmationTo = useSelector((state) => state.common.confirmationTo);
  const shiftsList = useSelector((state) => state.shifts.shiftsList);
  const userRoleId = useSelector((state) => state.login.userRoleId);
  const dynamicLeaves = useSelector(
    (state) => state.sampleLeaves.dynamicLeaves
  );

  /* ---- Local state ---- */
  const [formError, setFormError] = useState(null);
  const [selectedCollabs, setSelectedCollabs] = useState([]);

  /* ---- Derived hour settings ---- */
  let heureDebut = 9,
    heureFin = 17,
    heureMoyenne = 13;

  Object.values(settings.settingsList).forEach((item) => {
    if (item.label === "Heure début de journée")
      heureDebut = Number(item.settingValue);
    if (item.label === "Heure fin de journée")
      heureFin = Number(item.settingValue);

    let teamsFiltered =
      teams && teams.filter((t) => t.id == selectedFormData.id_team);
    if (teamsFiltered && teamsFiltered[0]) {
      let teamSettings = teamsFiltered[0].settings;
      teamSettings.forEach((s) => {
        if (s.label === "Heure début de journée")
          heureDebut = Number(s.value);
        if (s.label === "Heure fin de journée") heureFin = Number(s.value);
      });
    }
    heureMoyenne = (heureFin + heureDebut) / 2;
  });

  /* ---- Day period options ---- */
  function getFromDayPeriodOptions() {
    return [
      { value: heureDebut, label: "Matin" },
      { value: heureMoyenne, label: "Midi" },
    ];
  }

  function getToDayPeriodOptions() {
    return [
      { value: heureMoyenne, label: "Midi" },
      { value: heureFin, label: "Soir" },
    ];
  }

  function getSelectedDayPeriodName(dateTime) {
    let date = new Date(dateTime);
    let hour = date.getHours();
    if (hour < heureMoyenne) return "Matin";
    else if (hour > heureMoyenne) return "Soir";
    return "Midi";
  }

  function getSelectedDayPeriod(hour) {
    if (hour < 12) return settings.parameters["heure_debut_journee"];
    else if (hour > 12) return settings.parameters["heure_fin_journee"];
    return hour;
  }

  function getSelectedStandardDayPeriod(dateTime) {
    let date = new Date(dateTime);
    let hour = date.getHours();
    if (hour < heureMoyenne) return heureDebut;
    else if (hour > heureMoyenne) return heureFin;
    return heureMoyenne;
  }

  /* ---- Permission helpers ---- */
  function isManager() {
    return checkPermission(
      config.scopes.shifts,
      config.permissionTypes.edit,
      selectedFormData.id_team
    );
  }

  function canEditPlanning() {
    if (isOwner(userRoleId)) return true;
    if (!settings.blockageDay) return false;
    let now = moment();
    let monthEnd = moment(selectedDate, "x")
      .endOf("month")
      .add(settings.blockageDay, "day");
    return isManager() && now.isBefore(monthEnd);
  }

  /* ---- Data helpers ---- */
  function getTimeFromDate(dateTime) {
    return getMinutesFromDate(dateTime);
  }

  function getCollabGroupes() {
    let collab = collabs.collaboratorsList.filter(
      (c) => c.id === selectedFormData.id_collab
    )[0];
    return groupesList.filter((groupe) => {
      return collab && collab.groupeIds.includes(groupe.id);
    });
  }

  function getGroupeResources() {
    let uniqueIds = [];
    if (!selectedGroupe)
      return collabs.list.filter((c) => {
        if (
          c.id_team == selectedFormData.id_team &&
          !uniqueIds.includes(c.id) &&
          ["Présent", "Actif"].includes(c.statut)
        ) {
          uniqueIds.push(c.id);
          return true;
        }
        return false;
      });
    return resources.filter((r) => {
      if (
        r.groupeIds.includes(selectedFormData.id_groupe) &&
        !uniqueIds.includes(r.id)
      ) {
        uniqueIds.push(r.id);
        return true;
      }
      return false;
    });
  }

  function getAvailableCollabs() {
    let groupMemberIds = [];
    collabs.collaboratorsList
      .filter((resource) => {
        if (props.isFromGroupesView)
          return resource.groupeIds.includes(currentGroupeId);
        return resource.groupeIds.includes(selectedGroupe);
      })
      .forEach((resource) => groupMemberIds.push(resource.id));

    let groupes = groupesList.filter((g) => {
      if (props.isFromGroupesView) return g.id == currentGroupeId;
      return g.id == selectedGroupe;
    });

    let uniqueIds = [];
    return currentCollabsList.filter((collab) => {
      if (
        (groupes &&
          groupes[0] &&
          groupes[0].id_team != collab.id_team) ||
        groupMemberIds.includes(collab.id) ||
        uniqueIds.includes(collab.id) ||
        !["Présent", "Actif"].includes(collab.statut)
      )
        return false;
      uniqueIds.push(collab.id);
      return true;
    });
  }

  function getClientsOptions() {
    let options = [{ value: null, label: "Aucun" }];
    if (teams && teams.length) {
      options = options.concat(teams);
    }
    return options;
  }

  function getCollabShiftViewType() {
    if (selectedFormData.type == "s")
      return { value: "s", label: "Journée standard" };
    if (selectedFormData.type == "WE")
      return { value: "WE", label: "Forfait WE" };
    for (const key in settings.shiftTypes) {
      let option = settings.shiftTypes[key];
      if (option.value === selectedFormData.type) return option;
    }
    return null;
  }

  function getTeamLabelFromId(teamId) {
    if (teams && teams.length) {
      let filtered = teams.filter((t) => t.id == teamId);
      if (filtered.length) return filtered[0].label;
    }
    return "";
  }

  function getAssignedCollab() {
    let assigned = collabsList.filter(
      (c) => c.id == selectedFormData.id_collab
    );
    if (assigned[0] && assigned[0].label) return assigned[0].label;
    return "";
  }

  function getGroupLabel(groupId) {
    let group = groupesList.filter((g) => g.id == groupId)[0];
    if (group && group.label) return group.label;
    return "";
  }

  /* ---- Handlers ---- */
  function hide() {
    dispatch(hideFormPopup());
  }

  function validateForm() {
    let error = null;
    switch (selectedForm) {
      case "SHIFT":
        if (isWorkingShift(selectedFormData)) {
          let hoursDiff =
            (selectedFormData.to - selectedFormData.from) / (60 * 60 * 1000);
          if (hoursDiff >= 24) {
            error = "HOURS_EXCEED_LIMIT";
          }
        }
        break;
      case "SAMPLE_SHIFT":
      case "ADD_SAMPLE_SHIFT":
        if (!selectedFormData.task || selectedFormData.task == "")
          error = "MISSING_TASK";
        break;
      case "LEAVE":
      case "ADD_SAMPLE_LEAVE":
        if (!selectedFormData.type || selectedFormData.type == "")
          error = "MISSING_TYPE";
        break;
      default:
        break;
    }
    setFormError(error);
    return error == null;
  }

  function handleSave() {
    if (validateForm()) {
      switch (selectedForm) {
        case "SHIFT":
        case "LEAVE":
          if (shiftsList.length != 0) {
            for (const key in shiftsList) {
              if (shiftsList[key].id == selectedFormData.id) {
                dispatch(updateShiftLocally(selectedFormData));
              }
            }
          } else {
            dispatch(
              updateShifts({ items: [selectedFormData] })
            ).then((response) => {
              if (response["payload"]["responseData"]) {
                dispatch(updateShiftProperties(selectedFormData));
              }
            });
          }
          break;
        case "SAMPLE_SHIFT":
          dispatch(
            updateShiftSample({
              id: selectedFormData.id,
              type: selectedFormData.type,
              color: selectedFormData.color,
              task: selectedFormData.task,
              time_from: selectedFormData.timeFrom,
              time_to: selectedFormData.timeTo,
            })
          ).then(() => {
            dispatch(getShiftSamples());
          });
          break;
        default:
          break;
      }
      dispatch(hideFormPopup());
    }
  }

  function handleAdd() {
    if (validateForm()) {
      switch (selectedForm) {
        case "ADD_SAMPLE_SHIFT":
          dispatch(
            addShiftSample({
              type: selectedFormData["type"],
              color: selectedFormData["color"],
              task: selectedFormData["task"],
              time_from: selectedFormData["timeFrom"],
              time_to: selectedFormData["timeTo"],
            })
          ).then(() => {
            dispatch(getShiftSamples());
          });
          dispatch(hideFormPopup());
          break;
        case "ADD_SAMPLE_LEAVE":
          dispatch(addLeaveSample(selectedFormData));
          dispatch(hideFormPopup());
          break;
        case "ADD_USER":
          let members = [];
          selectedCollabs.forEach((collab) => {
            members.push({
              id_groupe: currentGroupeId,
              id_team: collab.id_team,
              id_collab: collab.id,
            });
            dispatch(
              addCollabToGroupe({ id: collab.id, id_groupe: selectedGroupe })
            );
          });
          if (members.length) {
            dispatch(addGroupeMembers({ items: members })).then(() => {
              dispatch(getMembers({ teamsIds: selectedTeamsIds }));
            });
            setSelectedCollabs([]);
            setFormError(null);
            dispatch(hideFormPopup());
          } else {
            setFormError("MISSING_COLLABS");
          }
          break;
        default:
          break;
      }
    }
  }

  function handleDelete() {
    dispatch(hideFormPopup());
    dispatch(
      showPopup({
        show: true,
        title: "CONFIRM_DELETE_SHIFT",
        message: "CONFIRM_DELETE_SHIFT_MSG",
      })
    );
  }

  const handleShiftTypeChange = (value) => {
    let sampleLeaveId = selectedFormData["id_sample_leave"];
    if (value == "R") {
      sampleLeaveId = sampleLeaves.filter((s) => s.type == value)[0].id;
    } else {
      sampleLeaveId = null;
    }
    dispatch(
      setSelectedFormData({
        ...selectedFormData,
        type: value,
        id_sample_leave: sampleLeaveId,
      })
    );
  };

  const handleClientChange = (value) => {
    let updatedData = { ...selectedFormData, id_team_delegate: value };
    if (!value) {
      updatedData.id_team = selectedFormData.selectedTeamId;
    }
    dispatch(setSelectedFormData(updatedData));
  };

  const handleLeaveTypeChange = (event) => {
    dispatch(
      setSelectedFormData({
        ...selectedFormData,
        type: event.target.value,
      })
    );
  };

  const handleGroupeChange = (value) => {
    dispatch(
      setSelectedFormData({ ...selectedFormData, id_groupe: value })
    );
  };

  const handleResourceChange = (value) => {
    dispatch(
      setSelectedFormData({ ...selectedFormData, id_collab: value })
    );
  };

  const handleTaskNameChange = (event) => {
    dispatch(
      setSelectedFormData({
        ...selectedFormData,
        task: event.target.value,
      })
    );
  };

  const handleNotesChange = (event) => {
    dispatch(
      setSelectedFormData({
        ...selectedFormData,
        description: event.target.value,
      })
    );
  };

  function handleSetAbsent(absent) {
    dispatch(
      setSelectedFormData({ ...selectedFormData, absent: absent })
    );
  }

  const handleStartDateChange = (time) => {
    let duration = selectedFormData.to - selectedFormData.from;
    const data = { ...selectedFormData, from: time };
    if (
      !["AST"].includes(selectedFormData.type) &&
      !dynamicLeaves[selectedFormData.type]
    ) {
      data.to = time + duration;
    }
    dispatch(setSelectedFormData(data));
  };

  const handleEndDateChange = (time) => {
    if (time > selectedFormData.from) {
      dispatch(
        setSelectedFormData({ ...selectedFormData, to: time })
      );
    }
  };

  const handleColorChange = (color) => {
    dispatch(
      setSelectedFormData({ ...selectedFormData, color: color })
    );
  };

  const onFromDayPeriodChange = (hour) => {
    switch (selectedForm) {
      case "LEAVE":
      case "ADD_SAMPLE_LEAVE":
        dispatch(
          setSelectedFormData({
            ...selectedFormData,
            timeFrom: hour * 60,
          })
        );
        break;
      default:
        break;
    }
  };

  const onToDayPeriodChange = (hour) => {
    switch (selectedForm) {
      case "LEAVE":
      case "ADD_SAMPLE_LEAVE":
        dispatch(
          setSelectedFormData({
            ...selectedFormData,
            timeTo: hour * 60,
          })
        );
        break;
      default:
        break;
    }
  };

  const onTimeFromChange = (time) => {
    switch (selectedForm) {
      case "SHIFT":
      case "LEAVE": {
        let fromDate = new Date(selectedFormData.from);
        let toDate = new Date(selectedFormData.to);
        fromDate.setHours(parseInt(time / 60));
        fromDate.setMinutes(parseInt(time % 60));
        if (fromDate < toDate) {
          dispatch(
            setSelectedFormData({
              ...selectedFormData,
              from: fromDate.getTime(),
            })
          );
        }
        break;
      }
      case "SAMPLE_SHIFT":
      case "ADD_SAMPLE_SHIFT":
      case "ADD_SAMPLE_LEAVE":
        dispatch(
          setSelectedFormData({ ...selectedFormData, timeFrom: time })
        );
        break;
      default:
        break;
    }
  };

  const onTimeToChange = (time) => {
    switch (selectedForm) {
      case "SHIFT":
      case "LEAVE": {
        let toDate = new Date(selectedFormData.to);
        let fromDate = new Date(selectedFormData.from);
        toDate.setHours(parseInt(time / 60));
        toDate.setMinutes(parseInt(time % 60));
        if (
          !["AST"].includes(selectedFormData.type) &&
          !dynamicLeaves[selectedFormData.type]
        ) {
          toDate.setDate(fromDate.getDate());
          if (toDate <= fromDate) {
            toDate.setDate(fromDate.getDate() + 1);
          }
        }
        dispatch(
          setSelectedFormData({
            ...selectedFormData,
            to: toDate.getTime(),
          })
        );
        break;
      }
      case "SAMPLE_SHIFT":
      case "ADD_SAMPLE_SHIFT":
      case "ADD_SAMPLE_LEAVE":
        dispatch(
          setSelectedFormData({ ...selectedFormData, timeTo: time })
        );
        break;
      default:
        break;
    }
  };

  const handleCollabsChange = (selectedList) => {
    setSelectedCollabs(selectedList);
    setFormError(null);
  };

  /* ---- Date input helpers ---- */
  function handleNativeDateFromChange(e) {
    const val = e.target.value;
    if (!val) return;
    const parts = val.split("-");
    let d = new Date(selectedFormData.from);
    d.setFullYear(parseInt(parts[0]));
    d.setMonth(parseInt(parts[1]) - 1);
    d.setDate(parseInt(parts[2]));
    handleStartDateChange(d.getTime());
  }

  function handleNativeDateToChange(e) {
    const val = e.target.value;
    if (!val) return;
    const parts = val.split("-");
    let d = new Date(selectedFormData.to);
    d.setFullYear(parseInt(parts[0]));
    d.setMonth(parseInt(parts[1]) - 1);
    d.setDate(parseInt(parts[2]));
    handleEndDateChange(d.getTime());
  }

  function handleNativeTimeFromChange(e) {
    const minutes = timeStrToMinutes(e.target.value);
    onTimeFromChange(minutes);
  }

  function handleNativeTimeToChange(e) {
    const minutes = timeStrToMinutes(e.target.value);
    onTimeToChange(minutes);
  }

  /* ---- Effects ---- */
  // Handle delete confirmation
  useEffect(() => {
    if (confirmationTo === "CONFIRM_DELETE_SHIFT") {
      switch (selectedForm) {
        case "SHIFT":
        case "LEAVE":
          dispatch(deleteShiftLocally(selectedFormData.id));
          dispatch(
            deleteShifts({ ids: [selectedFormData.id] })
          ).then((response) => {
            if (response["payload"]["responseData"]) {
              dispatch(
                deleteItemFromPlanningList(
                  response["payload"]["body"]["ids"][0]
                )
              );
            }
          });
          break;
        case "SAMPLE_SHIFT":
          dispatch(
            deleteShiftSamples({ ids: [selectedFormData.id] })
          ).then((response) => {
            if (response["payload"]["responseData"]) {
              dispatch(getShiftSamples());
            }
          });
          break;
        default:
          break;
      }
      dispatch(onPopupHandle());
    }
  }, [dispatch, confirmationTo]);

  // Reset on hide
  useEffect(() => {
    if (!props.show) {
      setSelectedCollabs([]);
      setFormError(null);
    }
  }, [props.show]);

  /* ---- Title resolver ---- */
  function getTitle() {
    switch (selectedForm) {
      case "SHIFT":
        return selectedFormData && selectedFormData.task
          ? selectedFormData.task
          : "Shift";
      case "LEAVE":
        return selectedFormData && selectedFormData.type
          ? selectedFormData.type
          : "Absence";
      case "SAMPLE_SHIFT":
        return selectedFormData && selectedFormData.task
          ? selectedFormData.task
          : "Modèle";
      case "ADD_SAMPLE_SHIFT":
        return "Ajouter modèle";
      case "ADD_SAMPLE_LEAVE":
        return "Ajouter absence";
      case "ADD_USER":
        return "Ajouter utilisateur";
      default:
        return "";
    }
  }

  /* ---- Status tag ---- */
  function getStatusChip() {
    const tag =
      selectedForm === "SHIFT" || selectedForm === "LEAVE" ||
      selectedForm === "SAMPLE_SHIFT"
        ? selectedFormData && selectedFormData.status
        : null;
    if (!tag) return null;
    const severity = getTagClass(tag);
    const color = severityToColor[severity] || "default";
    return (
      <Chip
        label={<Langs str={tag} />}
        size="small"
        color={color}
        sx={{
          fontWeight: 600,
          fontSize: "0.7rem",
          height: 24,
          borderRadius: "8px",
          ml: 1,
        }}
      />
    );
  }

  /* ---- Which action button set ---- */
  const isAddForm = ["ADD_SAMPLE_SHIFT", "ADD_SAMPLE_LEAVE", "ADD_USER"].includes(
    selectedForm
  );
  const isEditForm = ["SHIFT", "LEAVE", "SAMPLE_SHIFT"].includes(selectedForm);
  // Shift templates (sample shifts) are a Settings feature, so owners and
  // settings-edit users may add/edit/delete them even if they aren't managers.
  const canEditTemplates =
    isOwner(userRoleId) ||
    checkPermission(config.scopes.settings, config.permissionTypes.edit);
  const showEditButtons =
    (isEditForm && canEditPlanning()) ||
    (selectedForm === "SAMPLE_SHIFT" && canEditTemplates);
  const showAddButtons =
    selectedForm === "ADD_USER" ||
    (["ADD_SAMPLE_SHIFT", "ADD_SAMPLE_LEAVE"].includes(selectedForm) &&
      isManager()) ||
    (selectedForm === "ADD_SAMPLE_SHIFT" && canEditTemplates);

  let collabShiftType = getCollabShiftViewType();

  /* ================================================================ */
  /*  RENDER: Body router                                              */
  /* ================================================================ */
  function renderBody() {
    const shiftHandlers = {
      handleNativeDateFromChange,
      handleNativeDateToChange,
      handleNativeTimeFromChange,
      handleNativeTimeToChange,
      handleShiftTypeChange,
      handleLeaveTypeChange,
      handleGroupeChange,
      handleResourceChange,
      handleClientChange,
      handleSetAbsent,
      handleNotesChange,
      onTimeFromChange,
      onTimeToChange,
    };
    const shiftDataFns = {
      getSelectedStandardDayPeriod,
      getFromDayPeriodOptions,
      getToDayPeriodOptions,
      getCollabGroupes,
      getGroupeResources,
      getClientsOptions,
    };
    const sampleLeaveHandlers = {
      onTimeFromChange,
      onTimeToChange,
      handleLeaveTypeChange,
      onFromDayPeriodChange,
      onToDayPeriodChange,
    };
    const sampleLeaveDataFns = {
      getSelectedDayPeriod,
      getFromDayPeriodOptions,
      getToDayPeriodOptions,
    };

    switch (selectedForm) {
      case "SHIFT":
      case "LEAVE":
        return canEditPlanning() ? (
          <ShiftBody
            selectedFormData={selectedFormData}
            selectedForm={selectedForm}
            formError={formError}
            dynamicLeaves={dynamicLeaves}
            settings={settings}
            handlers={shiftHandlers}
            dataFns={shiftDataFns}
          />
        ) : (
          <CollabShiftView
            selectedFormData={selectedFormData}
            selectedForm={selectedForm}
            collabShiftType={collabShiftType}
            getTimeFromDate={getTimeFromDate}
            getSelectedDayPeriodName={getSelectedDayPeriodName}
            getGroupLabel={getGroupLabel}
            getAssignedCollab={getAssignedCollab}
            getTeamLabelFromId={getTeamLabelFromId}
          />
        );
      case "SAMPLE_SHIFT":
      case "ADD_SAMPLE_SHIFT":
        return (
          <SampleShiftBody
            selectedFormData={selectedFormData}
            formError={formError}
            settings={settings}
            dynamicLeaves={dynamicLeaves}
            handlers={{
              onTimeFromChange,
              onTimeToChange,
              handleShiftTypeChange,
              handleTaskNameChange,
            }}
            handleColorChange={handleColorChange}
          />
        );
      case "ADD_SAMPLE_LEAVE":
        return (
          <SampleLeaveBody
            selectedFormData={selectedFormData}
            formError={formError}
            settings={settings}
            handlers={sampleLeaveHandlers}
            dataFns={sampleLeaveDataFns}
            handleColorChange={handleColorChange}
          />
        );
      case "ADD_USER":
        return (
          <AddUserBody
            availableCollabs={getAvailableCollabs()}
            selectedCollabs={selectedCollabs}
            formError={formError}
            handleCollabsChange={handleCollabsChange}
          />
        );
      default:
        return null;
    }
  }

  /* ================================================================ */
  /*  MAIN RENDER                                                      */
  /* ================================================================ */
  return (
    <Dialog
      fullScreen
      open={!!props.show}
      onClose={hide}
      TransitionComponent={SlideUp}
      sx={{
        "& .MuiDialog-paper": {
          bgcolor: "#f5f5f7",
        },
      }}
    >
      {/* AppBar header */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          color: "text.primary",
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            minHeight: "56px !important",
          }}
        >
          <IconButton
            edge="start"
            onClick={hide}
            sx={{
              color: "text.primary",
              minWidth: 44,
              minHeight: 44,
              "&:active": { transform: "scale(0.92)" },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              maxWidth: "55%",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                fontSize: "1rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {getTitle()}
            </Typography>
            {getStatusChip()}
          </Box>

          {/* Right-side save shortcut for edit forms */}
          {showEditButtons && (
            <Button
              onClick={handleSave}
              sx={{
                minWidth: "auto",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.9rem",
                color: ORANGE,
                "&:active": { transform: "scale(0.95)" },
              }}
            >
              <Langs str="SAVE" />
            </Button>
          )}
          {showAddButtons && (
            <Button
              onClick={handleAdd}
              sx={{
                minWidth: "auto",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.9rem",
                color: ORANGE,
                "&:active": { transform: "scale(0.95)" },
              }}
            >
              <Langs str="ADD" />
            </Button>
          )}
          {!showEditButtons && !showAddButtons && <Box sx={{ width: 44 }} />}
        </Toolbar>
      </AppBar>

      {/* Scrollable body */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          pb: 10,
        }}
      >
        {renderBody()}
      </Box>

      {/* Sticky bottom bar */}
      <BottomBar
        showEditButtons={showEditButtons}
        showAddButtons={showAddButtons}
        onHide={hide}
        onSave={handleSave}
        onAdd={handleAdd}
        onDelete={handleDelete}
      />
    </Dialog>
  );
}
